import { ApiService } from "./api.service";
import type {
  IWorkspaceTemplate,
  IWorkspaceTemplateCreateInput,
  IWorkspaceTemplateVersion,
} from "@/types/workspace-template";

/**
 * Typed client for the workspace-templates endpoints (workspace-nested, mirroring
 * the other `/api/workspaces/<slug>/…` resources):
 *
 *  - `GET    …/templates/`        → usable templates (default-first).
 *  - `POST   …/templates/`        → capture the CURRENT workspace into a template.
 *  - `GET    …/templates/<id>/`   → one template.
 *  - `DELETE …/templates/<id>/`   → 204.
 *  - `GET    …/templates/<id>/versions/`                  → retained payload
 *    versions (newest-first). Read by the reset dialog's version picker.
 *  - `POST   …/templates/<id>/set-default/` → mark the template as the default
 *    new workspaces are seeded from (clears the previous default); returns the
 *    updated template.
 *
 * (The rollback endpoint `POST …/versions/<version>/activate/` still exists on the
 * backend but is no longer surfaced in the UI, so this client no longer wraps it.)
 *
 * Server-side: list/detail/versions are gated on `workspace.template.read`, and
 * every route shows the caller only the templates they may see: every `global`
 * one plus their own `private` ones (NG-1355). Create / delete / set-default
 * require `workspace.template.manage`, and set-default refuses a private template.
 */
export class WorkspaceTemplateService extends ApiService {
  async list(slug: string): Promise<IWorkspaceTemplate[]> {
    // The endpoint returns the standard cursor-paginated envelope (matching the
    // other workspace-nested list resources); templates are few, so we read the
    // single page's `results`.
    const page = await this.get<{ results: IWorkspaceTemplate[] }>(
      `/api/workspaces/${slug}/templates/`,
    );
    return page.results;
  }

  async retrieve(slug: string, templateId: string): Promise<IWorkspaceTemplate> {
    return this.get(`/api/workspaces/${slug}/templates/${templateId}/`);
  }

  async create(slug: string, input: IWorkspaceTemplateCreateInput): Promise<IWorkspaceTemplate> {
    return this.post(`/api/workspaces/${slug}/templates/`, input);
  }

  async remove(slug: string, templateId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/templates/${templateId}/`);
  }

  /** List a template's retained payload versions, newest-first. */
  async listVersions(slug: string, templateId: string): Promise<IWorkspaceTemplateVersion[]> {
    return this.get(`/api/workspaces/${slug}/templates/${templateId}/versions/`);
  }

  /** Mark a template as the default for new workspaces; returns the updated template. */
  async setDefault(slug: string, templateId: string): Promise<IWorkspaceTemplate> {
    return this.post(`/api/workspaces/${slug}/templates/${templateId}/set-default/`);
  }
}
